import { notifyError } from 'helpers/errorNotifier';
import { publicEnvConfig } from 'helpers/env';

export const getPathFromURL = (url, defaultOnError) => {
	const defaultValue = defaultOnError || '/';
	try {
		const urlObj = new URL(url, publicEnvConfig.BASE_URL);
		return `${urlObj.pathname}${urlObj.search}` || defaultValue;
	} catch (e) {
		return defaultValue;
	}
};

export const createAbsoluteUrlFromPath = (urlPath) => {
	let baseUrl = publicEnvConfig.BASE_URL;
	if (typeof window !== 'undefined' && window?.location?.origin) {
		baseUrl = window.location.origin;
	}
	return `${baseUrl}${urlPath}`;
};

export const ensureAbsoluteUrlIsOurDomain = (url) => {
	try {
		const urlObj = new URL(url);
		const baseUrlObj = new URL(publicEnvConfig.BASE_URL);

		if (urlObj.origin === baseUrlObj.origin) {
			return url;
		}

		urlObj.host = baseUrlObj.host;
		urlObj.protocol = baseUrlObj.protocol;
		return urlObj.toString();
	} catch (e) {
		if (couldBePath(url)) {
			return createAbsoluteUrlFromPath(url);
		}
		return '';
	}
};

export const couldBePath = (path) => path && path.startsWith('/');

export const ensurePathToBeValid = (path) => (couldBePath(path) ? path : `/${path}`);

export const getUtmParamsString = (params) => {
	return getAllowedParamsString(params, ['utm_']);
};
export const getUtmAndAFCParamsString = (params) => {
	return getAllowedParamsString(params, ['utm_', 'afc']);
};

export const createUrlFromParams = (params, filterEntries) => {
	if (!params) return '';
	try {
		const entries = filterEntries ? Object.entries(params).filter(filterEntries) : Object.entries(params);
		return entries.map((pair) => pair.map(encodeURIComponent).join('=')).join('&');
	} catch (error) {
		notifyError(`Error trying to exectue createUrlFromParams: ${error}`, {
			metadata: {
				query: params,
			},
		});
		return '';
	}
};

export const getAllowedParamsString = (params, allowedPrefixesArr = []) => {
	return createUrlFromParams(params, ([key]) =>
		allowedPrefixesArr.some((allowedPrefix) => key.startsWith(allowedPrefix)),
	);
};

export const stateToURLParams = (state) => {
	const params = new URLSearchParams();

	Object.entries(state).forEach(([key, value]) => {
		if (value !== null && value !== undefined && value !== '' && value !== false) {
			params.set(key, String(value));
		}
	});

	return params;
};

const parsePrimitiveValue = (value) => {
	if (value === 'true') return true;
	if (value === 'false') return false;

	return value;
};

export const parseSearchParamsToState = (searchParams, sortingKeys = []) => {
	const filters = {};
	const sorting = {};

	for (const [key, value] of searchParams.entries()) {
		const isSortingKey = sortingKeys.some((sortingKey) => key.startsWith(sortingKey));

		if (isSortingKey) {
			sorting[key] = parsePrimitiveValue(value);
		} else {
			filters[key] = parsePrimitiveValue(value);
		}
	}

	return { filters, sorting };
};

/**
 * Validates if the provided URL is valid
 * @param {string} url - The URL to validate
 * @returns {boolean} True if URL is valid, false otherwise
 */
export const validateUrl = (url) => {
	if (!url || typeof url !== 'string' || url.trim() === '') {
		return false;
	}

	// handling absolute and relative URLs
	try {
		if (url.startsWith('http://') || url.startsWith('https://')) {
			new URL(url);
		} else {
			// Handle relative URLs by using current origin
			new URL(url, window.location.origin);
		}
		return true;
	} catch {
		return false;
	}
};

export const isUrlIsFromAllowedDomains = ({ url: newUrl, allowedDomains, defaultReturn = false }) => {
	try {
		const url = new URL(newUrl);
		return allowedDomains.some((domain) => url.hostname.endsWith(domain));
	} catch (e) {
		// invalid URL, ignore
		return defaultReturn;
	}
};
